import { PriceSchemeNameInput } from '../../PriceSchemeNameInput';
import { useForm } from 'react-hook-form';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import {
  ImageUpload,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  useSnackbar,
  Typography,
  Checkbox,
} from '@weareopenr/component-library';
import { Fragment, useEffect, useState } from 'react';
import styles from './ImportSchemeStep.module.scss';
import { QueryKeys } from '../../../../hooks/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import CollapsibleSection from '../../../CollapsibleSection';
import { importPriceScheme } from '../../../../api/importPriceScheme/importPriceScheme';
import { usePriceScheme } from 'src/hooks/usePriceScheme';

export const ImportSchemeStep = ({
  onBack,
  onClose,
  priceSchemeId,
}: {
  onBack: () => void;
  onClose: () => void;
  priceSchemeId?: string;
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const formFields = useForm<{ name: string; file: string }>({ reValidateMode: 'onChange' });
  const {
    formState: { isValid, errors, isSubmitting },
    handleSubmit,
    setError,
    clearErrors,
    trigger,
  } = formFields;
  const [file, setFile] = useState<File | null>(null);
  const [apiRes, setApiRes] = useState<{ invalidChannelIds: string[]; duplicateChannelIds: string[] } | null>(null);
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false);
  const { data: priceScheme } = usePriceScheme(priceSchemeId);

  const isDisabled = (priceSchemeId && !isAcknowledged) || !isValid || !file;

  const onSubmit = async (data: { name: string; file: string }) => {
    try {
      const { success, invalidChannelIds, duplicateChannelIds } = await importPriceScheme(
        data.name,
        file,
        priceSchemeId,
      );
      if (success) {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.priceSchemes] });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.notifications] });
        showSnackbar({
          status: SnackbarStatus.SUCCESS,
          message: 'Your CSV is currently being processed, you will be notified when processing is complete.',
        });
        onClose();
      } else {
        setError('file', {
          message: 'File format does not match the accepted CSV format',
        });
        setApiRes({
          invalidChannelIds,
          duplicateChannelIds,
        });
      }
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'Something went wrong whilst uploading your CSV',
      });
    }
  };

  useEffect(() => {
    // removes the error when a new file is uploaded so it can be re-validated
    clearErrors('file');
    setApiRes(null);
    trigger();
  }, [file]);

  const channelErrors = apiRes?.invalidChannelIds?.length + apiRes?.duplicateChannelIds?.length;
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {priceSchemeId && (
        <Typography className={styles['price-scheme-title']}>
          Importing prices into - <span style={{ fontWeight: 'bold' }}>{priceScheme.name}</span>
        </Typography>
      )}

      {!priceSchemeId && <PriceSchemeNameInput formFields={formFields} />}
      <ImageUpload
        image={file}
        setImage={(value) => setFile(value instanceof File ? value : null)}
        prompt="Upload Pricing CSV"
        uploadType="csv"
        error={errors.file?.message}
      />

      {priceSchemeId && (
        <Checkbox
          checked={isAcknowledged}
          onChange={(event) => setIsAcknowledged(event.target.checked)}
          label="I acknowledge that any empty cells in the imported CSV will remove pricing override values."
        />
      )}

      {channelErrors > 0 && (
        <div className={styles['error-container']}>
          <CollapsibleSection title={`Channel Errors (${channelErrors} detected)`} number="!" size="small">
            <>
              {apiRes?.invalidChannelIds.length > 0 && (
                <>
                  <Typography size="small" weight="semi-bold">
                    Channels ID&apos;s not recognised
                  </Typography>
                  {apiRes?.invalidChannelIds.map((channelId) => (
                    <Fragment key={channelId}>
                      <Typography size="small">{channelId}</Typography>
                    </Fragment>
                  ))}
                </>
              )}
              {apiRes?.duplicateChannelIds.length > 0 && (
                <>
                  <Typography size="small" weight="semi-bold" className={styles['channel-error-subtitle']}>
                    Duplicate channel ID&apos;s
                  </Typography>
                  {apiRes?.duplicateChannelIds.map((channelId) => (
                    <Fragment key={channelId}>
                      <Typography size="small">{channelId}</Typography>
                    </Fragment>
                  ))}
                </>
              )}
            </>
          </CollapsibleSection>
        </div>
      )}
      <ButtonContainer
        left={
          <SecondaryButton type="button" buttonStyle="outline" size="full-width" onClick={onBack}>
            {priceSchemeId ? 'Cancel' : 'Back'}
          </SecondaryButton>
        }
        right={
          <PrimaryButton type="submit" size="full-width" disabled={isDisabled} loading={isSubmitting}>
            Import
          </PrimaryButton>
        }
      />
    </form>
  );
};
